import { useState, useEffect } from 'react';
import { Shield, ShieldOff, ChevronDown, Check } from 'lucide-react';
import { Button } from '../ui/Button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter } from '../ui/Dialog';
import { apiClient } from '../../lib/api-client';
import { Skeleton } from '../ui/Skeleton';
import { InlineConfirmation } from '../ui/InlineConfirmation';
import { DropdownMenu } from '../ui/DropdownMenu';

interface UserGroup {
  value: string;
  display: string;
}

interface ManageUserGroupsDialogProps {
  isOpen: boolean;
  userId: string;
  userEmail: string;
  onClose: () => void;
  onUpdate: () => void;
}

export function ManageUserGroupsDialog({
  isOpen,
  userId,
  userEmail,
  onClose,
  onUpdate
}: ManageUserGroupsDialogProps) {
  const [userGroups, setUserGroups] = useState<UserGroup[]>([]);
  const [allGroups, setAllGroups] = useState<any[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmingRemove, setConfirmingRemove] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [isOpen, userId]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [groupsRes, allGroupsRes] = await Promise.all([
        apiClient.get(`/api/admin/yaml-groups/user/${userEmail}`),
        apiClient.get('/api/admin/yaml-groups')
      ]);
      // Convert group names to UserGroup format
      const groupNames = (groupsRes as any).groups || [];
      setUserGroups(groupNames.map((name: string) => ({ value: name, display: name })));
      setAllGroups((allGroupsRes as any).groups || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddGroup = async () => {
    if (!selectedGroupId) return;

    setIsSubmitting(true);
    setError(null);
    try {
      await apiClient.post(`/api/admin/yaml-groups/${selectedGroupId}/members/${userEmail}`, {});
      setSelectedGroupId('');
      await fetchData();
      onUpdate();
    } catch (error) {
      console.error('Failed to add user to group:', error);
      setError('Failed to add user to group');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveGroup = async (groupName: string) => {
    setIsSubmitting(true);
    setError(null);
    try {
      await apiClient.delete(`/api/admin/yaml-groups/${groupName}/members/${userEmail}`);
      await fetchData();
      onUpdate();
    } catch (error) {
      console.error('Failed to remove user from group:', error);
      setError('Failed to remove user from group');
    } finally {
      setIsSubmitting(false);
      setConfirmingRemove(null);
    }
  };

  const userGroupIds = userGroups.map(g => g.value);
  const availableGroups = allGroups.filter(g => !userGroupIds.includes(g.id));
  const selectedGroupLabel = selectedGroupId || 'Select a group...';

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent maxWidth="2xl">
        <DialogHeader>
          <DialogTitle>Manage User Groups</DialogTitle>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{userEmail}</p>
        </DialogHeader>

        <DialogBody>
          {isLoading ? (
          <div className="space-y-4 py-4">
            <Skeleton className="h-10 w-full" />
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          </div>
        ) : (
          <>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Add User to Group
              </label>
              <div className="flex flex-col sm:flex-row gap-2">
                {/* Desktop native select */}
                <select
                  value={selectedGroupId}
                  onChange={(e) => setSelectedGroupId(e.target.value)}
                  className="hidden md:block flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white min-h-[44px]"
                  disabled={isSubmitting}
                >
                  <option value="">Select a group...</option>
                  {availableGroups.map(group => (
                    <option key={group.groupName} value={group.groupName}>
                      {group.groupName}
                    </option>
                  ))}
                </select>

                {/* Mobile bottom-sheet picker */}
                <div className="md:hidden w-full">
                  <DropdownMenu
                    className="w-full"
                    align="left"
                    trigger={
                      <button
                        type="button"
                        disabled={isSubmitting}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white min-h-[44px] flex items-center justify-between"
                      >
                        <span className="min-w-0 truncate">{selectedGroupLabel}</span>
                        <ChevronDown className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                      </button>
                    }
                    items={[
                      {
                        label: 'Select a group...',
                        icon: !selectedGroupId ? <Check className="w-4 h-4" /> : undefined,
                        onClick: () => setSelectedGroupId(''),
                      },
                      ...availableGroups.map((group: any) => ({
                        label: group.groupName,
                        icon: group.groupName === selectedGroupId ? <Check className="w-4 h-4" /> : undefined,
                        onClick: () => setSelectedGroupId(group.groupName),
                        className: 'font-mono',
                      })),
                    ]}
                  />
                </div>
                <Button
                  onClick={handleAddGroup}
                  disabled={!selectedGroupId || isSubmitting}
                  className="w-full sm:w-auto"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  Add
                </Button>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Current Groups ({userGroups.length})
              </h3>
              {userGroups.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 py-4">
                  User is not in any groups yet.
                </p>
              ) : (
                <div className="space-y-2">
                  {userGroups.map(group => (
                    <div
                      key={group.value}
                      className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-md min-h-[44px]"
                    >
                      <span className="text-sm text-gray-900 dark:text-white break-words flex-1 pr-2">
                        {group.display || group.value}
                      </span>
                      {confirmingRemove === group.value ? (
                        <InlineConfirmation
                          message="Remove user from this group?"
                          onConfirm={() => handleRemoveGroup(group.value)}
                          onCancel={() => setConfirmingRemove(null)}
                        />
                      ) : (
                        <button
                          onClick={() => setConfirmingRemove(group.value)}
                          disabled={isSubmitting}
                          className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-50 flex-shrink-0 p-2"
                        >
                          <ShieldOff className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {error && (
              <div className="mt-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-800 dark:text-red-200">
                {error}
              </div>
            )}
          </>
          )}
        </DialogBody>

        <DialogFooter>
              <Button variant="secondary" onClick={onClose}>
                Close
              </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

